/**
 * Name validation + derivation shared by `migrate` and `secret`. Pure
 * functions, no I/O — unit-tested directly.
 */

/**
 * Migration names are snake_case-ish: lowercase start, then lowercase
 * letters / digits / underscores. Mirrors a friendly prisma migration name
 * (`add_invoice_notes`). Max 60 chars keeps directory names sane.
 */
export const MIGRATION_NAME_PATTERN = /^[a-z][a-z0-9_]{0,59}$/;

export function isValidMigrationName(name: string): boolean {
  return MIGRATION_NAME_PATTERN.test(name);
}

export function migrationNameError(name: string): string {
  return (
    `"${name}" is not a valid migration name — use a short snake_case label ` +
    "(lowercase letters, digits and underscores; start with a letter), e.g. add_invoice_notes."
  );
}

/** Secret names are kebab-case: lowercase start, then lowercase/digits/hyphens. */
export const SECRET_NAME_PATTERN = /^[a-z][a-z0-9-]{0,60}$/;

export function isValidSecretName(name: string): boolean {
  return SECRET_NAME_PATTERN.test(name);
}

export function secretNameError(name: string): string {
  return (
    `"${name}" is not a valid secret name — use kebab-case ` +
    "(lowercase letters, digits and hyphens; start with a letter), e.g. openai-api-key."
  );
}

/** Derive the runtime env var name: openai-api-key → OPENAI_API_KEY. */
export function secretEnvVar(name: string): string {
  return name.replace(/-/g, '_').toUpperCase();
}

/**
 * Whether the app has a database enabled. The CLI's canonical shape is a
 * boolean (`appConfig.ts` normalizes to `database === true`), but the
 * Claude skills also describe a `{ needed: boolean }` object form, so accept
 * both: an app has a database only when database is `true` or `{needed:true}`.
 */
export function hasDatabase(database: unknown): boolean {
  if (database === true) return true;
  if (database && typeof database === 'object') {
    return (database as { needed?: unknown }).needed === true;
  }
  return false;
}

/**
 * Whether the app has FILE storage enabled (its private place for uploads,
 * images, documents — distinct from the database). Same shape rules as
 * hasDatabase: canonical boolean, `{needed:true}` accepted from the skills.
 */
export function hasFileStorage(fileStorage: unknown): boolean {
  if (fileStorage === true) return true;
  if (fileStorage && typeof fileStorage === 'object') {
    return (fileStorage as { needed?: unknown }).needed === true;
  }
  return false;
}
